// Libraries imports
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { connect } from "react-redux";


import { ReactComponent as DeleteIcon } from "../../../assets/images/delete1_WithCurrentColor.svg";
import { ReactComponent as ResetIcon } from "../../../assets/images/reset.svg";
import RuleIcon from '@mui/icons-material/Rule';

import { getRestaurantData, updateRestaurant } from "../../../api";
import { updateRestaurantData } from "../../../redux/actions/userDataActions";

// Component imports
import Container from "../../../components/Container";
import Loader from "../../../components/Loader";
import RestaurantHeader from "../../../components/RestaurantHeader";
import AlertMsg from "../../../components/AlertMsg";
import AppDialog from "../../../components/AppDialog";

import { CHARGE_TYPE, DELIVERY_CHARGES_FIELDS, GENERAL_CHARGES_FIELDS, getKeyStart, getItemKey } from "./constants";
import { validateDeliveryDistances, validateAmount } from "./ValidatorFunctions";
import DeliveryChargeCard from "./DeliveryChargeCard";
import ChargeCard from "./ChargeCard";

const SectionTitleBar = ({ title, btnText, addBtnHandler }) =>
    <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography>{title}</Typography>
        <Button sx={styles.addBtn} onClick={addBtnHandler}>
            {btnText}
        </Button>
    </Grid>;

const NoContentContainer = ({ message }) =>
    <Grid item xs={12} sx={styles.noContentContainer}>
        <Typography>{message}</Typography>
    </Grid>;

const Charges = ({ rest_data, updateRestaurantData }) => {
    const navigate = useNavigate();
    const { rest_id } = useParams();
    const [maxDeliveryRadius, setMaxDeliveryRadius] = useState(rest_data?.deliveryRadius ?? Number.MAX_SAFE_INTEGER);

    const [restaurantData, setRestaurantData] = useState(rest_data);
    const [deliveryCharges, setDeliveryCharges] = useState(
        rest_data?.deliveryCharge ? structuredClone(rest_data.deliveryCharge) : []);
    const [charges, setCharges] = useState(rest_data?.charges ? structuredClone(rest_data.charges) : []);
    const [errors, setErrors] = useState(new Map());
    const [changes, setChanges] = useState(new Set());

    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [msgAlert, setMsgAlert] = useState({ open: false });
    const [appDialogState, setAppDialogState] = useState({ open: false });
    const currencySymbol = restaurantData?.currecny ?? "£";

    useEffect(() => {
        fetchRestaurantData();
    }, []);

    const fetchRestaurantData = async () => {
        setIsLoading(true);
        const response = await getRestaurantData(rest_id);
        if (response.success) {
            const data = response.data;
            // Update the date in redux
            updateRestaurantData(data);
            setDeliveryCharges(structuredClone(data.deliveryCharge));
            setCharges(structuredClone(data.charges));
            setRestaurantData(data);
            setMaxDeliveryRadius(data.deliveryRadius ?? Number.MAX_SAFE_INTEGER);
        } else {
            setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        }
        setIsLoading(false);
    }

    const addCharge = (type) => () => {
        const tempId = crypto.randomUUID();
        if (type === CHARGE_TYPE.DELIVERY) {
            const newDeliveryCharges = [...deliveryCharges];
            const lastIndex = deliveryCharges.length - 1;
            // This tempId is to track the object when deleting it. The Save Button should be deactivated if this temp charge is deleted
            // NOTE: Make sure that the tempId is deleted before sending it to the backend
            if (lastIndex === -1) {
                newDeliveryCharges.push({ minDistance: 0, maxDistance: "", price: "", tempId });
                setDeliveryCharges(newDeliveryCharges);
            } else {
                const lastDeliveryCharge = deliveryCharges[lastIndex];
                if (lastDeliveryCharge.maxDistance === maxDeliveryRadius) {
                    setMsgAlert({ open: true, message: "You have already set charges for the maximum delivery radius", msgType: "error" });
                    return;
                }

                const maxErrorKey = getItemKey(lastIndex, DELIVERY_CHARGES_FIELDS.MAX, CHARGE_TYPE.DELIVERY);
                const priceErrorKey = getItemKey(lastIndex, DELIVERY_CHARGES_FIELDS.PRICE, CHARGE_TYPE.DELIVERY);

                if (!lastDeliveryCharge.maxDistance
                    || errors.has(maxErrorKey)
                    || lastDeliveryCharge.price === ""
                    || (lastIndex > 0 && !lastDeliveryCharge.price)
                    || errors.has(priceErrorKey)) {
                    if (!lastDeliveryCharge.maxDistance || errors.has(maxErrorKey)) {
                        setMsgAlert({ open: true, message: "Please enter a valid max distance before adding a new charge", msgType: "error" });
                        if (!lastDeliveryCharge.maxDistance) errors.set(maxErrorKey, "Max distance is required");
                    }

                    if (lastDeliveryCharge.price === ""
                        || (lastIndex > 0 && !lastDeliveryCharge.price)
                        || errors.has(priceErrorKey)) {
                        setMsgAlert({ open: true, message: "Please enter a valid price before adding a new charge", msgType: "error" });
                        if (!lastDeliveryCharge.price) errors.set(priceErrorKey, "Amount is required");
                    }
                    return;
                }

                newDeliveryCharges.push({
                    minDistance: lastDeliveryCharge.maxDistance ? Number(lastDeliveryCharge.maxDistance) : null,
                    maxDistance: "",
                    price: "",
                    tempId
                });
                setDeliveryCharges(newDeliveryCharges);
            }
            changes.add(tempId);
        } else {
            const lastIndex = charges.length - 1;
            if (charges.length > 0 && (!charges[lastIndex][GENERAL_CHARGES_FIELDS.NAME]
                || !charges[lastIndex][GENERAL_CHARGES_FIELDS.VALUE]
                || !charges[lastIndex][GENERAL_CHARGES_FIELDS.ORDER_TYPE]
            )) {
                setMsgAlert({ open: true, message: "Please enter a valid charge name, amount and order type before adding a new charge", msgType: "error" });
                if (!charges[lastIndex][GENERAL_CHARGES_FIELDS.NAME])
                    errors.set(getItemKey(lastIndex, GENERAL_CHARGES_FIELDS.NAME, CHARGE_TYPE.OTHER), "Charge name is required");
                if (!charges[lastIndex][GENERAL_CHARGES_FIELDS.VALUE])
                    validateAmount(charges[lastIndex][GENERAL_CHARGES_FIELDS.VALUE], GENERAL_CHARGES_FIELDS.VALUE, lastIndex, CHARGE_TYPE.OTHER, errors);
                if (!charges[lastIndex][GENERAL_CHARGES_FIELDS.ORDER_TYPE])
                    errors.set(getItemKey(lastIndex, GENERAL_CHARGES_FIELDS.ORDER_TYPE, CHARGE_TYPE.OTHER), "Order type is required");
                return;
            }
            setCharges([...charges, { name: "", value: "", percentage: false, active: false, customerVisibleMessage: "", tempId }]);
            changes.add(tempId);
        }
    }

    const handleDeliveryChargeChange = (value, index, field) => {
        if (isNaN(value)) return;

        const newDeliveryCharges = [...deliveryCharges];

        switch (field) {
            case DELIVERY_CHARGES_FIELDS.MAX:
                newDeliveryCharges[index].maxDistance = value;
                validateDeliveryDistances(value, index, newDeliveryCharges, errors, maxDeliveryRadius);
                break;
            case DELIVERY_CHARGES_FIELDS.PRICE:
                newDeliveryCharges[index].price = value;
                validateAmount(value, field, index, CHARGE_TYPE.DELIVERY, errors);
                break;
            default:
                break;
        }

        const changeKey = getItemKey(index, field, CHARGE_TYPE.DELIVERY);
        const isValueChanged = isDataChanged(field, value, index, CHARGE_TYPE.DELIVERY);
        if (isValueChanged) changes.add(changeKey);
        else changes.delete(changeKey);
        setDeliveryCharges(newDeliveryCharges);
    }

    const handleChargeChange = (value, index, field) => {
        const newCharges = [...charges];
        const errorKey = getItemKey(index, field, CHARGE_TYPE.OTHER);
        switch (field) {
            case GENERAL_CHARGES_FIELDS.NAME:
                newCharges[index].name = value;
                if (!value.trim()) errors.set(errorKey, "Charge name is required");
                else errors.delete(errorKey);
                break;
            case GENERAL_CHARGES_FIELDS.VALUE:
                if (isNaN(value)) return;
                newCharges[index].value = value;
                if (!value.trim()) {
                    errors.set(errorKey, "Amount is required");
                    break;
                }
                if (Number(value) <= 0) {
                    errors.set(errorKey, "Amount must be greater than 0");
                    break;
                }
                else errors.delete(errorKey);
                break;
            case GENERAL_CHARGES_FIELDS.ORDER_TYPE:
                newCharges[index].orderType = value;
                if (value) errors.delete(errorKey);
                break;
            case GENERAL_CHARGES_FIELDS.PERCENTAGE:
                // No validation as the request comes from a switch component
                newCharges[index].isPercentage = value;
                break;
            case GENERAL_CHARGES_FIELDS.ACTIVE:
                // No validation as the request comes from a switch component
                newCharges[index].isActive = value;
                break;
            case GENERAL_CHARGES_FIELDS.CUSTOMER_VISIBLE_MESSAGE:
                // No validation required for optional field
                newCharges[index].customerVisibleMessage = value;
                break;
            default:
                break;
        }
        const isValueChanged = isDataChanged(field, value, index, CHARGE_TYPE.OTHER);
        const changeKey = getItemKey(index, field, CHARGE_TYPE.OTHER);
        if (isValueChanged) changes.add(changeKey);
        else changes.delete(changeKey);
        setCharges(newCharges);
    }

    const deleteCharge = (index, chargeItem) => {
        if (chargeItem._id) {
            setAppDialogState({
                open: true,
                title: (
                    <Box>
                        <Typography sx={{ margin: 0 }}>
                            <DeleteIcon width={"2.5rem"} height={"2.5rem"} />
                        </Typography>
                        <Typography
                            variant="h2"
                            sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}
                        >
                            Delete Charge?
                        </Typography>
                    </Box>
                ),
                content: (
                    <Box>
                        Are you sure you want to delete the charge "{chargeItem.name}"?
                    </Box>
                ),
                firstBtnText: "Cancel",
                secondBtnText: "Delete",
                onClose: closeAppDialog,
                onConfirm: () => {
                    changes.add(chargeItem._id);
                    onConfirmDelete(index);
                },
            });
        } else {
            onConfirmDelete(index);
            const keyStart = getKeyStart(CHARGE_TYPE.OTHER, index);
            changes.delete(chargeItem.tempId);
            errors.forEach((value, key, map) => {
                if (key.startsWith(keyStart)) {
                    map.delete(key);
                }
            });
        }
        // remove errors for the deleted charge from GENERAL_CHARGES_FIELDS.
        Object.keys(GENERAL_CHARGES_FIELDS).forEach(field => errors.delete(getItemKey(index, field, CHARGE_TYPE.OTHER)));
    }

    const onConfirmDelete = (index) => {
        const newCharges = [...charges];
        newCharges.splice(index, 1);
        setCharges(newCharges);
        closeAppDialog();
    }

    const closeAppDialog = () => {
        setAppDialogState({ ...appDialogState, open: false });
    }

    const isDataChanged = (field, value, index, chargeType) => {
        if (chargeType === CHARGE_TYPE.DELIVERY) {
            const previousValue = restaurantData.deliveryCharge[index]?.[field];
            // Please don't change to `previouseValue != value`. It is to handle the boolean comparison
            return !(previousValue == value);
        } else {
            const previousValue = restaurantData.charges[index]?.[field];
            return !(previousValue == value);
        }
    }

    const deleteDeliveryCharge = () => {
        if (deliveryCharges.length) {
            const tempId = deliveryCharges[deliveryCharges.length - 1].tempId;
            if (tempId) changes.delete(tempId);
            else changes.add([deliveryCharges.length - 1]._id);
            setDeliveryCharges(deliveryCharges.slice(0, - 1));

            const keyStart = getKeyStart(CHARGE_TYPE.OTHER, deliveryCharges.length - 1);
            errors.forEach((value, key, map) => {
                if (key.startsWith(keyStart)) {
                    map.delete(key);
                }
            });
        }
    }

    const onCancel = () => {
        setAppDialogState({
            open: true,
            title: (
                <Box>
                    <Typography sx={{ margin: 0 }}>
                        <ResetIcon width={"3rem"} height={"3rem"} />
                    </Typography>
                    <Typography
                        variant="h2"
                        sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}
                    >
                        Reset Changes?
                    </Typography>
                </Box>
            ),
            content: (
                <Box>
                    Are you sure you want to undo all changes?
                </Box>
            ),
            firstBtnText: "Cancel",
            secondBtnText: "Reset",
            onClose: closeAppDialog,
            onConfirm: () => reset(),
        });
    }

    const reset = () => {
        setDeliveryCharges(structuredClone(restaurantData.deliveryCharge));
        setCharges(structuredClone(restaurantData.charges));
        errors.clear();
        changes.clear();
        closeAppDialog();
    }

    const saveChanges = () => {
        if (errors.size) return;

        // Validating the last row of charges
        const lastDeliveryIndex = deliveryCharges.length - 1;
        const lastDeliveryCharge = lastDeliveryIndex > -1 ? deliveryCharges[lastDeliveryIndex] : null;

        const lastChargeIndex = charges.length - 1;
        const lastCharge = lastChargeIndex > -1 ? charges[lastChargeIndex] : null;

        if ((lastDeliveryCharge && (!lastDeliveryCharge[DELIVERY_CHARGES_FIELDS.MAX] || !lastDeliveryCharge[DELIVERY_CHARGES_FIELDS.PRICE]))
            || (lastCharge && (!lastCharge[GENERAL_CHARGES_FIELDS.NAME] || !lastCharge[GENERAL_CHARGES_FIELDS.VALUE] || !lastCharge[GENERAL_CHARGES_FIELDS.ORDER_TYPE]))) {

            if (lastDeliveryCharge) {
                validateDeliveryDistances(lastDeliveryCharge.maxDistance, lastDeliveryIndex, deliveryCharges, errors, maxDeliveryRadius);
                validateAmount(lastDeliveryCharge.price, DELIVERY_CHARGES_FIELDS.PRICE, lastDeliveryIndex, CHARGE_TYPE.DELIVERY, errors);
            }

            if (lastCharge) {
                if (!lastCharge[GENERAL_CHARGES_FIELDS.NAME]) errors.set(getItemKey(lastChargeIndex, GENERAL_CHARGES_FIELDS.NAME, CHARGE_TYPE.OTHER), "Name is required");
                validateAmount(lastCharge.value, GENERAL_CHARGES_FIELDS.VALUE, lastChargeIndex, CHARGE_TYPE.OTHER, errors);
                if (!lastCharge[GENERAL_CHARGES_FIELDS.ORDER_TYPE]) errors.set(getItemKey(lastChargeIndex, GENERAL_CHARGES_FIELDS.ORDER_TYPE, CHARGE_TYPE.OTHER), "Order type is required");
                setErrors(new Map(errors));
                setMsgAlert({ open: true, message: "Please clear all the errors before saving", msgType: "error" });
            }

            setErrors(new Map(errors));
            return;
        }

        const updatedDeliveryCharges = deliveryCharges.map(charge => { delete charge.tempId; return charge });
        const updatedCharges = charges.map(charge => { delete charge.tempId; return charge });
        const data = { deliveryCharge: updatedDeliveryCharges, charges: updatedCharges };

        const minRangeLimit = lastDeliveryCharge?.[DELIVERY_CHARGES_FIELDS.MAX] || 0;
        const isMissingRange = maxDeliveryRadius !== Number.MAX_SAFE_INTEGER ? maxDeliveryRadius !== minRangeLimit : false;

        if (isMissingRange) displayMissingRange(minRangeLimit, maxDeliveryRadius, data);
        else updateChargesData(data);
    }

    const displayMissingRange = (min, max, updateData) => {
        setAppDialogState({
            open: true,
            title: (
                <Box>
                    <Typography sx={{ margin: 0 }}>
                        <RuleIcon width={"2.5rem"} height={"2.5rem"} sx={{ fontSize: "2.5rem" }} />
                    </Typography>
                    <Typography
                        variant="h2"
                        sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}
                    >
                        Missing Delivery Charges
                    </Typography>
                </Box>
            ),
            content: (
                <Box>
                    You don't have delivery charges set for the range of <span style={styles.missingMilesText}> {min} to {max} miles.</span>
                    Are you sure you want to proceed?
                </Box>
            ),
            firstBtnText: "Cancel",
            secondBtnText: "Proceed",
            onClose: closeAppDialog,
            onConfirm: () => {
                updateChargesData(updateData);
            },
        });
    }

    const updateChargesData = async (data) => {
        setIsUpdating(true);
        const response = await updateRestaurant(data, rest_id);
        if (response.success) {
            setChanges(new Set());
            setErrors(new Map());
            setMsgAlert({ open: true, message: "Changes saved successfully", msgType: "success" });
            const newResponse = await getRestaurantData(rest_id);
            // Update the data in redux store silently
            if (newResponse.success) {
                const newRestaurantData = newResponse.data;
                updateRestaurantData(newRestaurantData);
                setDeliveryCharges(structuredClone(newRestaurantData.deliveryCharge));
                setCharges(structuredClone(newRestaurantData.charges));
                setRestaurantData(newRestaurantData);
            } else {
                setMsgAlert({ open: true, message: "Failed to update Restaurant data in the local store. Please refresh the page to get the latest data", msgType: "error" });
            }
        } else {
            setMsgAlert({ open: true, message: "Failed to save changes. Please try again.", msgType: "error" });
        }
        setIsUpdating(false);
        closeAppDialog();
    }

    const saveBtnDisabled = errors.size > 0 || changes.size === 0;

    return (
        <Container
            page={"restaurants"}
            searchVal={""}
            onSearch={() => null}
            searchLabel=""
            p={1.5}
        >
            <Grid container>
                <Grid item xs={12} mb={2}>
                    <RestaurantHeader
                        title={"Charges"}
                        onBackBtnClick={() => navigate("/restaurant/" + rest_id)}
                    />
                </Grid>
                {<Grid
                    container
                    item
                    xs={12}
                    sx={styles.mainView}
                    px={3}
                    py={2}
                >
                    {isLoading ? <Box sx={styles.loaderContainer}><Loader /></Box> : <>
                        <Grid item xs={12} mx={2} mb={4}>
                            <SectionTitleBar title={"Delivery Charges"} btnText={"Add Delivery Charge"} addBtnHandler={addCharge(CHARGE_TYPE.DELIVERY)} />
                            {deliveryCharges.length ? <Box sx={{ mb: 1 }}>{deliveryCharges.map((charge, index) =>
                                <DeliveryChargeCard
                                    key={index}
                                    index={index}
                                    chargeData={charge}
                                    onInputChange={handleDeliveryChargeChange}
                                    errors={errors}
                                    chargeCount={deliveryCharges.length}
                                    maxRange={maxDeliveryRadius}
                                    currencySymbol={currencySymbol}
                                />)}
                                <Box textAlign={"right"}>
                                    <Button
                                        onClick={deleteDeliveryCharge}
                                        sx={{
                                            ...styles.addBtn,
                                            backgroundColor: '#f9560d !important',
                                            opacity: 0.85,
                                            '&: hover': {
                                                opacity: 1,
                                                backgroundColor: '#f9560d !important',
                                            },
                                        }}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <DeleteIcon stroke="#ffffff" width={20} height={20} />
                                            <span>Delete Last Row</span>
                                        </Box>
                                    </Button>
                                </Box>
                            </Box> : <NoContentContainer message={"No Delivery Charges"} />}
                        </Grid>

                        <Grid item xs={12} mx={2}>
                            <SectionTitleBar title={"Charges"} btnText={"Add Charge"} addBtnHandler={addCharge(CHARGE_TYPE.OTHER)} />
                            {charges.length ? <>{charges.map((charge, index) =>
                                <ChargeCard
                                    key={index}
                                    index={index}
                                    chargeData={charge}
                                    onInputChange={handleChargeChange}
                                    onDelete={deleteCharge}
                                    errors={errors}
                                    chargeCount={charges.length}
                                />)
                            }</>
                                : <NoContentContainer message={"No Charges Added"} />}
                        </Grid>

                        <Box sx={styles.btnMainView} mt={2}>
                            <Button
                                type="button"
                                sx={styles.cancelBtn}
                                onClick={onCancel}
                                disabled={changes.size === 0}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                disabled={saveBtnDisabled}
                                sx={{
                                    ...styles.saveBtn,
                                    opacity: saveBtnDisabled ? 0.2 : 1,
                                }}
                                onClick={saveChanges}
                            >
                                Save
                            </Button>
                        </Box>
                    </>}
                </Grid>}
            </Grid>

            <AppDialog
                open={appDialogState.open}
                onClose={appDialogState.onClose}
                content={appDialogState.content}
                title={appDialogState.title}
                firstBtnContent={appDialogState.firstBtnText}
                secondBtnContent={appDialogState.secondBtnText}
                firstBtnClickHandler={appDialogState.onClose}
                secondBtnClickHandler={appDialogState.onConfirm}
                options={{ btnLoading: isUpdating }}
            />

            <AlertMsg
                msgAlert={msgAlert}
                onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
            />
        </Container>
    )
}

const mapStateToProps = state => ({
    rest_data: state.userData.restaurantData
});

const mapDispatchToProps = dispatch => ({
    updateRestaurantData: (data) => dispatch(updateRestaurantData(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Charges);

const styles = {
    mainView: {
        backgroundColor: "#ffffff",
        minHeight: "calc(100vh - 180px)",
        borderRadius: "8px"
    },
    loaderContainer: {
        width: "100%",
        minHeight: "calc(100vh - 180px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    },
    missingMilesText: {
        fontWeight: 'bold',
        backgroundColor: '#f9560d',
        padding: '0.125rem 0.5rem',
        borderRadius: '8px',
        color: 'white',
        whiteSpace: 'nowrap',
        marginRight: '0.5rem'
    },
    addBtn: {
        backgroundColor: "#000 !important",
        color: "#FFF",
        fontFamily: "GilroyBold",
        fontSize: 14,
        borderRadius: 1,
        textTransform: "capitalize",
        paddingLeft: '1rem',
        paddingRight: '1rem',
        width: '12rem',
        "&:hover": {
            backgroundColor: "#000 !important",
        },
    },
    noContentContainer: {
        background: "#eee",
        padding: "1rem",
        textAlign: "center",
        marginBottom: "1rem",
        borderRadius: "8px",
        height: '6rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    btnMainView: {
        display: "flex",
        justifyContent: "flex-end",
        flexDirection: "row",
        width: "100%",
        flexWrap: "wrap",
        gap: "1rem",
        padding: "1rem",
        boxSizing: "border-box"
    },
    saveBtn: {
        backgroundColor: "#000000",
        textTransform: "capitalize",
        fontFamily: "GilroySemiBold",
        fontSize: "15px",
        borderRadius: "9px",
        height: "44px",
        width: "100%",
        maxWidth: '18rem',
        color: "#ffffff !important",
        "&:hover": {
            backgroundColor: "#000000 !important",
            opacity: 0.7
        },
    },
    cancelBtn: {
        border: "1px solid #000000",
        textTransform: "capitalize",
        fontFamily: "GilroySemiBold",
        fontSize: "15px",
        color: "#000000",
        borderRadius: "9px",
        height: "44px",
        width: "100%",
        maxWidth: '18rem',
    }
}
